import React from 'react';
import { 
  Box, 
  Button, 
  CircularProgress,
  Tooltip,
  useTheme 
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import DownloadIcon from '@mui/icons-material/Download';
import { alpha } from '@mui/material/styles';
import { reviewThemeExtensions } from '../../theme/reviewTheme';

const ActionButtons = ({
  isFullyVerified,
  isLocking,
  needsMcqAnswer,
  validating,
  onUploadImage,
  onCompile,
  onLock,
  onUnlock,
  compileType = 'question',
  modifierKey = 'Ctrl',
  onShowOriginal,
  showingOriginal = false,
  // Generate exam props
  onGenerateExam,
  generatingExam = false,
  examGenerationTaskId = null,
  examGenerationStatus = null,
  hasVerifiedQuestions = false,
  onRedownloadExam
}) => {
  const theme = useTheme();
  const { spacing } = reviewThemeExtensions;
  return (
    <Box sx={{ 
      display: 'flex', 
      justifyContent: 'space-between', 
      alignItems: 'center', 
      flexWrap: 'wrap', 
      gap: spacing.md, 
      mt: spacing.md 
    }}>
      {/* Left side buttons */}
      <Box sx={{ display: 'flex', gap: spacing.md }}>
        <Button
          variant="outlined"
          size="medium"
          startIcon={<CloudUploadIcon />}
          onClick={onUploadImage}
          disabled={isFullyVerified}
          sx={reviewThemeExtensions.components.actionButton(theme)}
        >
          Upload Image
        </Button>
        <Button 
          variant="outlined" 
          size="medium"
          onClick={onCompile}
          disabled={validating || isFullyVerified}
          sx={reviewThemeExtensions.components.actionButton(theme)}
        >
          Check LaTeX
        </Button>
        <Button
          variant="outlined"
          size="medium"
          onClick={onShowOriginal}
          startIcon={showingOriginal ? <VisibilityOffIcon /> : <VisibilityIcon />}
          sx={reviewThemeExtensions.components.actionButton(theme)}
        >
          {showingOriginal ? 'Hide' : 'Show'} Original
        </Button>
      </Box>
      
      {/* Right side buttons */}
      <Box sx={{ display: 'flex', gap: spacing.md }}>
        {/* Generate Exam Button - Always visible */}
        {onGenerateExam && (
          <>
            {examGenerationTaskId && !generatingExam ? (
              // Show download button if exam was already generated
              <Tooltip 
                title="Download the previously generated exam"
                placement="top"
              >
                <Button
                  variant="outlined"
                  size="large"
                  onClick={onRedownloadExam}
                  startIcon={<DownloadIcon />}
                  sx={{ 
                    ...reviewThemeExtensions.components.actionButton(theme),
                    px: 3,
                    py: 1.5,
                    fontWeight: 'bold',
                    borderColor: theme.palette.success.main,
                    color: theme.palette.success.main,
                    '&:hover': {
                      borderColor: theme.palette.success.dark,
                      backgroundColor: alpha(theme.palette.success.main, 0.1)
                    }
                  }}
                >
                  DOWNLOAD EXAM
                </Button>
              </Tooltip>
            ) : (
              // Show generate button
              <Tooltip 
                title={
                  !hasVerifiedQuestions 
                    ? "No questions have been reviewed yet. Review and lock questions to enable exam generation."
                    : generatingExam
                    ? examGenerationStatus || "Generating exam..."
                    : "Generate an exam using only the reviewed questions from this document"
                }
                placement="top"
              >
                <span>
                  <Button
                    variant="outlined"
                    size="large"
                    onClick={onGenerateExam}
                    disabled={!hasVerifiedQuestions || generatingExam}
                    startIcon={generatingExam ? <CircularProgress size={20} /> : <AssignmentTurnedInIcon />}
                    sx={{ 
                      ...reviewThemeExtensions.components.actionButton(theme),
                      px: 3,
                      py: 1.5,
                      fontWeight: 'bold',
                      borderColor: hasVerifiedQuestions ? theme.palette.success.main : theme.palette.grey[400],
                      color: hasVerifiedQuestions ? theme.palette.success.main : theme.palette.grey[600],
                      '&:hover': hasVerifiedQuestions ? {
                        borderColor: theme.palette.success.dark,
                        backgroundColor: alpha(theme.palette.success.main, 0.1)
                      } : {},
                      '&.Mui-disabled': {
                        borderColor: theme.palette.grey[300],
                        color: theme.palette.grey[500]
                      }
                    }}
                  >
                    {generatingExam ? 'GENERATING...' : 'GENERATE EXAM'}
                  </Button>
                </span>
              </Tooltip>
            )}
          </>
        )}
        
        {isFullyVerified && (
          <Button
            variant="outlined"
            size="large"
            onClick={onUnlock}
            disabled={isLocking}
            startIcon={isLocking ? <CircularProgress size={20} /> : null}
            sx={{ 
              ...reviewThemeExtensions.components.actionButton(theme),
              px: 3,
              py: 1.5,
              fontWeight: 'bold',
              borderColor: theme.palette.warning.main,
              color: theme.palette.warning.main,
              '&:hover': {
                borderColor: theme.palette.warning.dark,
                backgroundColor: alpha(theme.palette.warning.main, 0.1)
              }
            }}
          >
            {isLocking ? 'UNLOCKING...' : 'UNLOCK FOR EDITING'}
          </Button>
        )}
        
        {!isFullyVerified && (
          <Tooltip 
            title={needsMcqAnswer ? 'Please select an MCQ answer before locking' : `${modifierKey}+L`}
            placement="top"
          >
            <span>
              <Button
                variant="contained"
                size="large"
                onClick={onLock}
                disabled={isLocking || needsMcqAnswer}
                startIcon={isLocking ? <CircularProgress size={20} /> : null}
                sx={{ 
                  ...reviewThemeExtensions.components.actionButton(theme),
                  px: 4,
                  py: 1.5,
                  fontWeight: 'bold',
                  backgroundColor: needsMcqAnswer 
                    ? theme.palette.warning.main 
                    : theme.palette.primary.main,
                  '&:hover': {
                    backgroundColor: needsMcqAnswer
                      ? theme.palette.warning.dark
                      : theme.palette.primary.dark
                  },
                  '&.Mui-disabled': {
                    backgroundColor: needsMcqAnswer 
                      ? theme.palette.warning.light 
                      : undefined
                  }
                }}
              >
                {isLocking 
                  ? 'LOCKING...' 
                  : needsMcqAnswer 
                    ? 'SELECT MCQ ANSWER FIRST' 
                    : 'LOCK IN TO DATABASE'}
              </Button>
            </span>
          </Tooltip>
        )}
      </Box>
    </Box>
  );
};

export default ActionButtons;